import * as React from 'react';

import moment from 'moment';

import type {QuoteDisplayStatus} from 'nutshell-graphql-types';

import type {QuoteTimes} from '../../../../../../ui/lead/sales-documents/shared';

import {
    DownloadIcon,
    EyeIcon,
    HtmlLinkIcon,
    CopyIcon,
    AcceptedIcon,
    MinusCircleIcon,
    ArchiveIcon,
    EmailIcon,
    InvoiceIcon,
    TimesCircleIcon,
    WarningIcon,
    DocumentReadyIcon,
    SendIcon,
    NavIconSalesIcon,
    DraftIcon,
    ClockIcon,
} from '../../icon';
import type {SelectMenuOption} from '../../select-menu/select-menu';

import type {SalesDocumentType} from './types';

export const getQuoteActionMenuOptions = (
    quoteId: string,
    quoteLink: string,
    slug: string,
    status: QuoteDisplayStatus,
    downloadQuote: (slug: string) => void,
    duplicateQuote: (id: string) => void,
    updateQuoteStatus: (id: string, status: QuoteDisplayStatus) => void,
    sendQuoteViaEmail: () => void,
    createInvoiceFromQuote: (id: string) => void
): SelectMenuOption[] => {
    const removeOption = {
        label: 'Remove',
        icon: <ArchiveIcon size={15} wrapWithDiv={false} />,
        handleSelect: () => {
            updateQuoteStatus(quoteId, 'ARCHIVED');
        },
    } as const;

    const duplicateOption = {
        label: 'Make a copy',
        icon: <CopyIcon size={15} wrapWithDiv={false} />,
        handleSelect: () => {
            duplicateQuote(quoteId);
        },
    } as const;

    const options = [
        {
            label: 'View as customer',
            icon: <EyeIcon size={15} wrapWithDiv={false} />,
            handleSelect: () => {
                window.open(quoteLink, '_blank');
            },
        },
        {
            label: 'Copy link',
            icon: <HtmlLinkIcon size={15} wrapWithDiv={false} />,
            handleSelect: () => {
                navigator.clipboard.writeText(quoteLink);
            },
        },
        {
            label: 'Download a PDF',
            icon: <DownloadIcon size={15} wrapWithDiv={false} />,
            handleSelect: () => {
                if (slug) {
                    downloadQuote(slug);
                }
            },
        },
        duplicateOption,
    ];

    if (status === 'REVOKED') {
        return [duplicateOption, removeOption];
    } else if (status === 'ARCHIVED') {
        return [duplicateOption];
    } else if (status === 'PUBLISHED') {
        options.push(
            {
                label: 'Send via email',
                icon: <EmailIcon size={15} wrapWithDiv={false} />,
                handleSelect: () => {
                    sendQuoteViaEmail();
                },
            },
            {
                label: 'Revoke',
                icon: <MinusCircleIcon size={15} wrapWithDiv={false} />,
                handleSelect: () => {
                    updateQuoteStatus(quoteId, 'REVOKED');
                },
            }
        );
    } else if (status === 'DRAFT') {
        options.push(removeOption);
    } else if (status === 'SENT') {
        options.push({
            label: 'Mark as accepted',
            icon: <AcceptedIcon size={15} wrapWithDiv={false} />,
            handleSelect: () => {
                updateQuoteStatus(quoteId, 'ACCEPTED');
            },
        });
    } else if (status === 'ACCEPTED') {
        options.push({
            label: 'Create an invoice',
            icon: <InvoiceIcon size={15} wrapWithDiv={false} />,
            handleSelect: () => {
                createInvoiceFromQuote(quoteId);
            },
        });
    }

    return options;
};

export const getInvoiceActionMenuOptions = (
    invoiceId: string,
    invoiceLink: string,
    slug: string,
    status: QuoteDisplayStatus,
    downloadInvoice: (slug: string) => void,
    duplicateInvoice: (id: string) => void,
    updateInvoiceStatus: (id: string, status: QuoteDisplayStatus) => void,
    sendInvoiceViaEmail: () => void
): SelectMenuOption[] => {
    const duplicateOption = {
        label: 'Make a copy',
        icon: <CopyIcon size={15} wrapWithDiv={false} />,
        handleSelect: () => {
            duplicateInvoice(invoiceId);
        },
    } as const;

    const removeOption = {
        label: 'Remove',
        icon: <ArchiveIcon size={15} wrapWithDiv={false} />,
        handleSelect: () => {
            updateInvoiceStatus(invoiceId, 'ARCHIVED');
        },
    } as const;

    const voidOption = {
        icon: <TimesCircleIcon size={15} wrapWithDiv={false} />,
        label: 'Void',
        handleSelect: () => {
            updateInvoiceStatus(invoiceId, 'REVOKED');
        },
    } as const;

    const options = [
        {
            label: 'View as customer',
            icon: <EyeIcon size={15} wrapWithDiv={false} />,
            handleSelect: () => {
                window.open(invoiceLink, '_blank');
            },
        },
        {
            label: 'Copy link',
            icon: <HtmlLinkIcon size={15} wrapWithDiv={false} />,
            handleSelect: () => {
                navigator.clipboard.writeText(invoiceLink);
            },
        },
        {
            label: 'Download a PDF',
            icon: <DownloadIcon size={15} wrapWithDiv={false} />,
            handleSelect: () => {
                if (slug) {
                    downloadInvoice(slug);
                }
            },
        },
        duplicateOption,
    ];

    if (status === 'REVOKED') {
        return [duplicateOption, removeOption];
    } else if (status === 'ARCHIVED') {
        return [duplicateOption];
    } else if (status === 'DRAFT') {
        options.push(removeOption);
    } else if (status === 'PUBLISHED') {
        options.push(
            {
                label: 'Send via email',
                icon: <EmailIcon size={15} wrapWithDiv={false} />,
                handleSelect: () => {
                    sendInvoiceViaEmail();
                },
            },
            voidOption
        );
    } else if (status === 'SENT') {
        options.push(voidOption);
    }

    return options;
};

export const getSalesDocumentStatusText = (
    status: QuoteDisplayStatus,
    type: SalesDocumentType,
    invoiceOverdueDuration?: string | null
) => {
    if (invoiceOverdueDuration) {
        return `Overdue`;
    }

    switch (status) {
        case 'PUBLISHED':
            return 'Ready';
        case 'SENT':
            return 'Sent';
        case 'ACCEPTED':
            return type === 'Quote' ? 'Accepted' : 'Paid';
        case 'ARCHIVED':
            return 'Removed';
        case 'REVOKED':
            return type === 'Quote' ? 'Revoked' : 'Voided';
        case 'DRAFT':
            return 'Draft';
        case 'EXPIRED':
            return 'Expired';
        default:
            return '';
    }
};

export function getIcon(
    status: QuoteDisplayStatus,
    type: SalesDocumentType,
    invoiceOverdueDuration?: string | null
): React.ComponentType<any> {
    if (invoiceOverdueDuration) {
        return WarningIcon;
    }

    switch (status) {
        case 'PUBLISHED':
            return DocumentReadyIcon;
        case 'SENT':
            return SendIcon;
        case 'ACCEPTED':
            return type === 'Quote' ? AcceptedIcon : NavIconSalesIcon;
        case 'ARCHIVED':
            return ArchiveIcon;
        case 'REVOKED':
            return type === 'Quote' ? MinusCircleIcon : TimesCircleIcon;

        case 'EXPIRED':
            return type === 'Quote' ? ClockIcon : WarningIcon;
        case 'DRAFT':
        default:
            return DraftIcon;
    }
}

export const getBadgeLevel = (
    status: QuoteDisplayStatus,
    invoiceOverdueDuration?: string | null
) => {
    if (invoiceOverdueDuration) {
        return 'alert';
    }

    switch (status) {
        case 'DRAFT':
            return 'hidden';
        case 'PUBLISHED':
            return 'info';
        case 'SENT':
            return 'info';
        case 'ACCEPTED':
            return 'success';
        case 'ARCHIVED':
            return 'hidden';
        case 'REVOKED':
            return 'hidden';
        case 'EXPIRED':
            return 'alert';
        default:
            return 'hidden';
    }
};

const formatTime = (datetime: number): string => {
    const datetimeValue = moment.unix(datetime);

    return datetimeValue.isValid() ? datetimeValue.format('MMM Do, YYYY') : '';
};

export const getSalesDocumentStatusTime = (
    status: QuoteDisplayStatus,
    type: SalesDocumentType,
    salesDocumentTimes: QuoteTimes,
    invoiceOverdueDuration?: string | null
) => {
    if (invoiceOverdueDuration) {
        return `Overdue by ${invoiceOverdueDuration}`;
    }

    switch (status) {
        case 'PUBLISHED':
            if (salesDocumentTimes.publishedAt > 0) {
                return `Ready ${formatTime(salesDocumentTimes.publishedAt)}`;
            } else {
                return '';
            }
        case 'SENT':
            if (salesDocumentTimes.sentAt > 0) {
                return `Sent ${formatTime(salesDocumentTimes.sentAt)}`;
            } else {
                return '';
            }
        case 'ACCEPTED':
            if (salesDocumentTimes.acceptedAt > 0) {
                return `${type === 'Quote' ? 'Accepted' : 'Paid'} ${formatTime(
                    salesDocumentTimes.acceptedAt
                )}`;
            } else {
                return '';
            }
        case 'DRAFT':
            return `Drafted ${formatTime(salesDocumentTimes.createdAt)}`;
        case 'EXPIRED':
        case 'REVOKED':
        case 'ARCHIVED':
        default:
            return '';
    }
};
